import { debounce, throttle } from "es-toolkit";
import * as React from "react";

/** Union of every modelled map; used only where there is no target to key on. */
type EventMap = WindowEventMap & HTMLElementEventMap & DocumentEventMap;

/** Every target the hook can subscribe to. */
type EventListenerTarget = HTMLElement | Window | Document | ServiceWorkerContainer;

/**
 * The names legal on one specific target. Ordered most specific first: the
 * members overlap structurally, so a `Window` would otherwise match `Document`.
 *
 * A union target narrows to the names common to both, since `keyof` over a
 * union of maps is the intersection of their keys. That is the safe direction —
 * only names valid on every member survive.
 */
type TargetEventMap<T> = T extends Window
  ? WindowEventMap
  : T extends Document
    ? DocumentEventMap
    : T extends ServiceWorkerContainer
      ? ServiceWorkerContainerEventMap
      : T extends HTMLElement
        ? HTMLElementEventMap
        : never;

type UseEventListenerOptions = AddEventListenerOptions & {
  /**
   * Invoke the handler at most once per this many milliseconds (leading +
   * trailing). The throttle window survives re-renders and any pending
   * trailing call is cancelled on unmount.
   */
  throttleMs?: number;
  /**
   * Invoke the handler once the event has been quiet for this many
   * milliseconds (trailing only). Use this when the handler measures a
   * settled state — a resize drag that should re-measure when it stops —
   * and `throttleMs` when it should keep running during the gesture.
   * Any pending call is cancelled on unmount. Mutually exclusive with
   * `throttleMs`; passing both uses `throttleMs`.
   */
  debounceMs?: number;
};

/**
 * Subscribe to a DOM event for the lifetime of the calling component or hook.
 *
 * `element` is optional and has no default. A nullish `element` means **do not
 * attach**: nothing is subscribed, and an existing subscription is torn down.
 * There is no implicit fallback to `window` or `document`, so the target a
 * listener actually lands on is always readable at the call site.
 *
 * Two consequences worth knowing:
 *
 * - To listen on a global, pass `window` / `document`. In shared or
 *   server-rendered code, use `globalThis.window` / `globalThis.document`:
 *   absent properties evaluate to undefined instead of throwing during render.
 * - To gate a subscription on state, make the target nullish —
 *   `isOpen ? document : null`. That replaces the old `enabled` option,
 *   which no longer exists.
 *
 * A target that resolves later must live in state, not a ref: the hook
 * re-subscribes when the target it was passed changes, and a ref mutation is
 * not a render.
 *
 * `eventName` is checked against the target. `controllerchange` is legal on
 * `navigator.serviceWorker` and nowhere else; `resize` is legal on `window` and
 * not on a service-worker container. The handler's parameter is typed from the
 * same pair, so `message` on a container gives you a `MessageEvent`.
 */
// A statically nullish target gives inference nothing to work with, so `T` would
// collapse to `never` and reject every name. Nothing attaches in that case, so the
// name is checked against the union instead. `isOpen ? document : null` does not
// land here — `T` still infers `Document`.
export function useEventListener<K extends keyof EventMap>(
  eventName: K,
  handler: null | ((event: EventMap[K]) => void),
  element?: null,
  options?: boolean | UseEventListenerOptions,
): void;
export function useEventListener<T extends EventListenerTarget, K extends keyof TargetEventMap<T> & string>(
  eventName: K,
  handler: null | ((event: TargetEventMap<T>[K]) => void),
  element?: T | null,
  options?: boolean | UseEventListenerOptions,
): void;
export function useEventListener<T extends EventListenerTarget, K extends keyof TargetEventMap<T> & string>(
  eventName: K,
  handler: null | ((event: TargetEventMap<T>[K]) => void),
  element?: T | null,
  options?: boolean | UseEventListenerOptions,
) {
  const savedHandler = React.useRef(handler);

  React.useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  // Destructure so inline option objects don't re-subscribe on every render.
  const { capture, once, passive, signal, throttleMs, debounceMs } =
    typeof options === "boolean" ? ({ capture: options } satisfies UseEventListenerOptions) : (options ?? {});

  React.useEffect(() => {
    // No target means no subscription. This is the supported way to express a
    // gate, and the reason there is no `enabled` option.
    if (!element) {
      return;
    }

    if (!element.addEventListener) {
      // eslint-disable-next-line no-console -- pre-existing violation
      console.warn("useEventListener listening to element without addEventListener", element);
      return;
    }

    const invokeHandler: EventListener = (...args) => {
      /* istanbul ignore else -- @preserve - defensive check */
      if (savedHandler.current) {
        // @ts-expect-error Type 'Event' is missing the following properties from type 'DeviceMotionEvent': acceleration, accelerationIncludingGravity, interval, rotationRatets
        savedHandler.current(...args);
      }
    };

    if (throttleMs && debounceMs) {
      // Documented as mutually exclusive; warn rather than silently dropping one.
      // eslint-disable-next-line no-console -- matches the target warning below
      console.warn(
        "useEventListener received both throttleMs and debounceMs; using throttleMs and ignoring debounceMs",
        { eventName, throttleMs, debounceMs },
      );
    }

    const scheduledHandler = throttleMs
      ? throttle(invokeHandler, throttleMs)
      : debounceMs
        ? debounce(invokeHandler, debounceMs)
        : null;
    const eventListener: EventListener = scheduledHandler ?? invokeHandler;

    element.addEventListener(eventName, eventListener, { capture, once, passive, signal });

    return () => {
      element.removeEventListener(eventName, eventListener, { capture });
      scheduledHandler?.cancel();
    };
  }, [eventName, element, capture, once, passive, signal, throttleMs, debounceMs]);
}
